import Keycloak from "keycloak-js";

// Session persistence keys
const SESSION_KEYS = {
  TOKEN: 'keycloak_token',
  REFRESH_TOKEN: 'keycloak_refresh_token',
  ID_TOKEN: 'keycloak_id_token',
  AUTHENTICATED: 'keycloak_authenticated',
  TOKEN_PARSED: 'keycloak_token_parsed',
};

// Save tokens to localStorage
const saveTokens = (tokens) => {
  if (typeof window === "undefined") return;
  
  try {
    if (tokens.token) localStorage.setItem(SESSION_KEYS.TOKEN, tokens.token);
    if (tokens.refreshToken)
      localStorage.setItem(SESSION_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    if (tokens.idToken) localStorage.setItem(SESSION_KEYS.ID_TOKEN, tokens.idToken);
    localStorage.setItem(SESSION_KEYS.AUTHENTICATED, "true");
    if (tokens.tokenParsed) {
      localStorage.setItem(
        SESSION_KEYS.TOKEN_PARSED,
        JSON.stringify(tokens.tokenParsed)
      );
    }
  } catch (error) {
    console.error("Failed to save tokens to localStorage:", error);
  }
};

// Load tokens from localStorage
const loadTokens = () => {
  if (typeof window === "undefined") return null;
  
  try {
    const token = localStorage.getItem(SESSION_KEYS.TOKEN);
    const refreshToken = localStorage.getItem(SESSION_KEYS.REFRESH_TOKEN);
    const idToken = localStorage.getItem(SESSION_KEYS.ID_TOKEN);
    const authenticated = localStorage.getItem(SESSION_KEYS.AUTHENTICATED) === 'true';
    const tokenParsedStr = localStorage.getItem(SESSION_KEYS.TOKEN_PARSED);

    if (token && refreshToken && authenticated) {
      const tokenParsed = tokenParsedStr ? JSON.parse(tokenParsedStr) : null;
      
      // Check if token is still valid (not expired)
      if (tokenParsed && tokenParsed.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (tokenParsed.exp > now) {
          return { token, refreshToken, idToken, authenticated: true, tokenParsed };
        } else {
          clearTokens();
        }
      }
    }
  } catch (error) {
    console.error('Failed to load tokens from localStorage:', error);
    clearTokens();
  }
  return null;
};

// Clear tokens from localStorage
const clearTokens = () => {
  if (typeof window === "undefined") return;
  
  try {
    Object.values(SESSION_KEYS).forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clear tokens from localStorage:', error);
  }
};

// Keycloak configuration from environment variables
const hostname = typeof window !== "undefined" ? window.location.hostname : "";
const AIMAPS_ISSUER = "https://ui.auth.delhivery.com/realms/delhivery";
const hostOverrides = {
  "maps-ui-dev.pntrzz.com": {
    clientId: "maps-prod-ui",
    issuer: "https://key-cloak.delhivery.com/auth/realms/delhivery",
  },
  "aimaps.delhivery.com": {
    clientId: "maps-web-app",
    url: "https://ui.auth.delhivery.com/",
    realm: "delhivery",
    issuer: AIMAPS_ISSUER,
    redirectUri: "https://aimaps.delhivery.com/internal",
  },
  "maps.delhivery.com": {
    clientId: "maps-v2-web-app",
    url: "https://ui.auth.delhivery.com/",
    realm: "delhivery",
    issuer: AIMAPS_ISSUER,
    redirectUri: "https://maps.delhivery.com/internal",
  },
  "localhost": {
    clientId: "local-web-app",
    url: "https://ui.auth.delhivery.com/",
    realm: "dlv-staging",
    issuer: "https://ui.auth.delhivery.com/realms/dlv-staging",
  },
};

const getHostOverride = (host) => {
  if (hostOverrides[host]) return hostOverrides[host];
  if (host === "aimaps.delhivery.com" || host.endsWith(".aimaps.delhivery.com")) {
    return hostOverrides["aimaps.delhivery.com"];
  }
  if (host === "maps.delhivery.com" || host.endsWith(".maps.delhivery.com")) {
    return hostOverrides["maps.delhivery.com"];
  }
  return {};
};

const hostOverride = getHostOverride(hostname);
const defaultRedirectUri =
  hostOverride.redirectUri ||
  process.env.REACT_APP_KEYCLOAK_REDIRECT_URI ||
  (typeof window !== "undefined" ? `${window.location.origin}/` : "");

const keycloakConfig = {
  url:
    hostOverride.url ||
    process.env.REACT_APP_KEYCLOAK_URL ||
    "https://key-cloak.delhivery.com/auth/",
  realm:
    hostOverride.realm ||
    process.env.REACT_APP_KEYCLOAK_REALM ||
    "delhivery",
  clientId:
    hostOverride.clientId ||
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID ||
    "maps-prod-ui",
  issuer:
    hostOverride.issuer ||
    process.env.REACT_APP_KEYCLOAK_ISSUER ||
    "https://key-cloak.delhivery.com/auth/realms/delhivery",
};

// Create Keycloak instance
let keycloak;
if (keycloakConfig.url && keycloakConfig.realm && keycloakConfig.clientId) {
  keycloak = new Keycloak(keycloakConfig);
} else {
  // Dummy instance for build time
  keycloak = new Keycloak({ url: "http://localhost", realm: "dummy", clientId: "dummy" });
  console.warn("Keycloak configuration missing. Using dummy instance.");
}

// Handle token expiration
keycloak.onTokenExpired = () => {
  console.log('Token expired, attempting refresh');
  if (!keycloak.refreshToken) {
    console.warn('No refresh token, redirecting to login');
    clearTokens();
    keycloak.login();
    return;
  }
  keycloak.updateToken(70)
    .then((refreshed) => {
      if (refreshed) {
        console.log('Token refreshed successfully');
        if (keycloak.token) {
          saveTokens({
            token: keycloak.token,
            refreshToken: keycloak.refreshToken,
            idToken: keycloak.idToken,
            tokenParsed: keycloak.tokenParsed,
          });
        }
      } else {
        console.warn('Token not refreshed, valid for ' + Math.round(keycloak.tokenParsed.exp + keycloak.timeSkew - new Date().getTime() / 1000) + ' seconds');
      }
    })
    .catch(() => {
      console.error('Token refresh failed');
      clearTokens();
      keycloak.login();
    });
};

// Persist tokens immediately after successful authentication
keycloak.onAuthSuccess = () => {
  if (keycloak.token && keycloak.refreshToken) {
    saveTokens({
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
      idToken: keycloak.idToken,
      tokenParsed: keycloak.tokenParsed,
    });
  }
};

// Override the init method to restore session
const originalInit = keycloak.init;
keycloak.init = function (initOptions) {
  const restoredSession = loadTokens();

  // Check if we're in OAuth callback
  const isCallback = typeof window !== 'undefined' && 
                     window.location.hash && 
                     (window.location.hash.includes('access_token') || 
                      window.location.hash.includes('code='));

  const effectiveOptions = {
    ...initOptions,
    // Only use check-sso during OAuth callback or if explicitly set
    onLoad: initOptions.onLoad || (isCallback ? 'check-sso' : undefined),
    checkLoginIframe: false,
    enableLogging: true,
    pkceMethod: 'S256',
    responseMode: 'query',
    flow: 'standard',
  };

  if (restoredSession) {
    keycloak.token = restoredSession.token;
    keycloak.refreshToken = restoredSession.refreshToken;
    keycloak.idToken = restoredSession.idToken;
    keycloak.tokenParsed = restoredSession.tokenParsed;
    keycloak.authenticated = true;
  }

  return originalInit
    .call(this, effectiveOptions)
    .then((auth) => {
      if (auth && keycloak.token && keycloak.refreshToken) {
        saveTokens({
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
          idToken: keycloak.idToken,
          tokenParsed: keycloak.tokenParsed,
        });
      }
      return auth;
    })
    .catch((err) => {
      console.error('Keycloak init failed', err);
      clearTokens();
      return false;
    });
};

// Override the updateToken method to save tokens
const originalUpdateToken = keycloak.updateToken;
keycloak.updateToken = function (minValidity) {
  return originalUpdateToken.call(this, minValidity).then((refreshed) => {
    if (refreshed) {
      saveTokens({
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken,
        tokenParsed: keycloak.tokenParsed,
      });
    }
    return refreshed;
  });
};

// Override the logout method to clear tokens
const originalLogout = keycloak.logout;
keycloak.logout = function (options) {
  clearTokens();
  return originalLogout.call(this, options);
};

// Override the login method to ensure redirectUri is set correctly
const originalLogin = keycloak.login;
keycloak.login = function (options) {
  const redirectUri = options?.redirectUri || defaultRedirectUri;
  
  const loginOptions = {
    ...options,
    redirectUri: redirectUri,
  };
  
  return originalLogin.call(this, loginOptions);
};

export const KEYCLOAK_REDIRECT_URI = defaultRedirectUri;
export default keycloak;
